import React, { useState } from 'react';
import { processarImagemOCR, validarArquivoOCR, DICAS_OCR_MANUSCRITOS } from '../services/ocrService';
import { API_CONFIG } from '../services/staticDataConfig';

interface OCRUploadModalProps {
  show: boolean;
  onClose: () => void;
  onTextoExtraido: (texto: string) => void;
  pacienteId: number;
  pacienteNome?: string;
}

interface ProgressoState {
  status: 'carregando' | 'reconhecendo' | 'concluido' | 'idle' | 'preprocessando';
  progresso: number;
  mensagem: string;
}

type MetodoOCR = 'tesseract' | 'gemini';

export default function OCRUploadModal({ 
  show, 
  onClose, 
  onTextoExtraido,
  pacienteId,
  pacienteNome 
}: OCRUploadModalProps) {
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [processando, setProcessando] = useState(false);
  const [progresso, setProgresso] = useState<ProgressoState>({
    status: 'idle',
    progresso: 0,
    mensagem: ''
  });
  const [erro, setErro] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [modeloSelecionado, setModeloSelecionado] = useState<string>('gemini-3-flash-preview');

  // ========================================
  // Manipular seleção de arquivo
  // ========================================
  const handleArquivoSelecionado = (e: React.ChangeEvent<HTMLInputElement>) => {
    const arquivoSelecionado = e.target.files?.[0];
    
    if (!arquivoSelecionado) {
      return;
    }

    // Validar arquivo
    const validacao = validarArquivoOCR(arquivoSelecionado);
    if (!validacao.valido) {
      setErro(validacao.erro || 'Arquivo inválido');
      setArquivo(null);
      setPreviewUrl(null);
      return;
    }

    setErro(null);
    setArquivo(arquivoSelecionado);

    // Gerar preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(arquivoSelecionado);
  };

  // ========================================
  // Processar OCR com Tesseract.js
  // ========================================
  const handleProcessarOCR = async () => {
    if (!arquivo) {
      setErro('Nenhum arquivo selecionado');
      return;
    }

    try {
      setProcessando(true);
      setErro(null);

      console.log('[OCR-MODAL] Iniciando processamento de:', arquivo.name);

      // Processar imagem com OCR
      const resultado = await processarImagemOCR(arquivo, (prog) => {
        setProgresso({
          status: prog.status,
          progresso: prog.progresso,
          mensagem: prog.mensagem
        });
      });

      // Validar resultado
      if (!resultado.texto || resultado.texto.trim().length === 0) {
        throw new Error('Nenhum texto foi reconhecido na imagem. Tente com uma imagem mais nítida ou veja as dicas abaixo.');
      }

      if (resultado.confianca < 30) {
        alert('⚠️ Atenção: O texto foi extraído com baixa confiança. Revise cuidadosamente o conteúdo.');
      }

      // Retornar texto extraído
      onTextoExtraido(resultado.texto);

      // Log de sucesso
      console.log('[OCR-MODAL] Texto extraído com sucesso:', {
        caracteres: resultado.totalCaracteres,
        confianca: resultado.confianca,
        tempo: resultado.tempoProcessamento
      });

      // Mostrar mensagem de sucesso
      alert(`✅ Texto extraído com sucesso!\n\n📊 Estatísticas:\n- Caracteres: ${resultado.totalCaracteres}\n- Confiança: ${resultado.confianca.toFixed(1)}%\n- Tempo: ${(resultado.tempoProcessamento / 1000).toFixed(1)}s`);

      // Fechar modal após sucesso
      setTimeout(() => {
        handleFechar();
      }, 500);
    } catch (error: any) {
      console.error('[OCR-MODAL] Erro ao processar OCR:', error);
      setErro(error.message || 'Erro ao processar documento');
    } finally {
      setProcessando(false);
    }
  };

  // ========================================
  // Processar OCR com Google Gemini Vision
  // ========================================
  const handleProcessarGemini = async () => {
    if (!arquivo) {
      setErro('Nenhum arquivo selecionado');
      return;
    }

    try {
      setProcessando(true);
      setErro(null);
      setProgresso({
        status: 'carregando',
        progresso: 20,
        mensagem: 'Enviando imagem para processamento...'
      });

      console.log('[GEMINI-MODAL] Iniciando processamento de:', arquivo.name);

      // Validar pacienteId
      if (!pacienteId || pacienteId <= 0) {
        throw new Error('ID do paciente inválido');
      }

      // Preparar FormData
      const formData = new FormData();
      formData.append('imagem', arquivo);
      formData.append('paciente_id', pacienteId.toString());
      formData.append('modelo', modeloSelecionado);

      // Obter token
      const token = sessionStorage.getItem('token');
      if (!token) {
        throw new Error('Token de autenticação não encontrado');
      }

      setProgresso({
        status: 'reconhecendo',
        progresso: 50,
        mensagem: 'Processando com IA multimodal...'
      });

      // Fazer requisição para API backend
      const baseURL = API_CONFIG.BASE_URL;
      const response = await fetch(`${baseURL}/ocr/gemini`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.mensagem || 'Erro ao processar com Gemini');
      }

      const resultado = await response.json();

      setProgresso({
        status: 'concluido',
        progresso: 100,
        mensagem: 'Texto extraído com sucesso!'
      });

      // Validar resultado
      if (!resultado.texto || resultado.texto.trim().length === 0) {
        throw new Error('Nenhum texto foi reconhecido na imagem. Tente com uma imagem mais nítida.');
      }

      // Retornar texto extraído
      onTextoExtraido(resultado.texto);

      // Log de sucesso
      console.log('[GEMINI-MODAL] Texto extraído com sucesso:', {
        caracteres: resultado.totalCaracteres,
        confianca: resultado.confianca,
        tempo: resultado.tempoProcessamento,
        tokens: resultado.tokensUsados,
        modelo: resultado.modelo
      });

      // Mostrar mensagem de sucesso
      alert(`✅ Texto extraído com sucesso!

📊 Estatísticas:
- Caracteres: ${resultado.totalCaracteres}
- Confiança: ${resultado.confianca.toFixed(1)}%
- Tempo: ${(resultado.tempoProcessamento / 1000).toFixed(1)}s`);

      // Fechar modal após sucesso
      setTimeout(() => {
        handleFechar();
      }, 500);
    } catch (error: any) {
      console.error('[GEMINI-MODAL] Erro ao processar com Gemini:', error);
      setErro(error.message || 'Erro ao processar documento');
    } finally {
      setProcessando(false);
    }
  };

  // ========================================
  // Fechar modal e limpar estado
  // ========================================
  const handleFechar = () => {
    setArquivo(null);
    setPreviewUrl(null);
    setProgresso({ status: 'idle', progresso: 0, mensagem: '' });
    setErro(null);
    setProcessando(false);
    onClose();
  };

  if (!show) return null;

  return (
    <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className="fas fa-file-image me-2"></i>
              Digitalizar Documento (OCR)
            </h5>
            <button 
              type="button" 
              className="btn-close btn-close-white" 
              onClick={handleFechar}
              disabled={processando}
            ></button>
          </div>

          {/* Body */}
          <div className="modal-body">
            {/* Informações do paciente */}
            {pacienteNome && (
              <div className="alert alert-info mb-3">
                <i className="fas fa-user me-2"></i>
                <strong>Paciente:</strong> {pacienteNome}
              </div>
            )}

            {/* Seletor de modelo Gemini */}
            <div className="mb-3">
              <label className="form-label fw-bold" htmlFor="ocrUploadModeloIa">
                <i className="fas fa-brain me-2"></i>
                Modelo de IA
              </label>
              <select 
                id="ocrUploadModeloIa"
                className="form-select" 
                value={modeloSelecionado}
                onChange={(e) => setModeloSelecionado(e.target.value)}
                disabled={processando}
              >
                <option value="gemini-3-flash-preview">Sophya 3 Flash Preview (Recomendado)</option>
                <option value="gemini-2.5-flash">Sophya 2.5 Flash (Rápido)</option>
                <option value="gemini-2.5-flash-lite">Sophya 2.5 Lite (Mais Leve)</option>
              </select>
              <small className="form-text text-muted">
                Sistema tenta automaticamente outros modelos se o selecionado não estiver disponível
              </small>
            </div>

            {/* Instruções */}
            <div className="alert alert-warning mb-3">
              <i className="fas fa-info-circle me-2"></i>
              <strong>Como usar:</strong>
              <ul className="mb-0 mt-2">
                <li>Envie uma foto ou scan do documento clínico (manuscrito ou digitado)</li>
                <li>O texto será automaticamente extraído e adicionado ao campo <strong>"Observações"</strong></li>
                <li>Você poderá editar e mover partes do texto para outros campos</li>
                <li>A imagem <strong>NÃO será salva</strong> no sistema (apenas o texto)</li>
              </ul>
            </div>

            {/* Upload de arquivo */}
            <div className="mb-3">
              <label className="form-label fw-bold" htmlFor="ocrUploadArquivo">
                <i className="fas fa-upload me-2"></i>
                Selecionar Documento
              </label>
              <input
                id="ocrUploadArquivo"
                type="file"
                className="form-control"
                accept="image/jpeg,image/jpg,image/png,image/bmp,image/tiff"
                onChange={handleArquivoSelecionado}
                disabled={processando}
              />
              <small className="form-text text-muted">
                Formatos aceitos: JPG, PNG, BMP, TIFF | Tamanho máximo: 10 MB
              </small>
            </div>

            {/* Preview da imagem */}
            {previewUrl && (
              <div className="mb-3">
                <div className="form-label fw-bold">
                  <i className="fas fa-image me-2"></i>
                  Preview do Documento
                </div>
                <div className="text-center border rounded p-2" style={{ backgroundColor: '#f8f9fa' }}>
                  <img 
                    src={previewUrl} 
                    alt="Preview" 
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '400px', 
                      objectFit: 'contain',
                      border: '1px solid #ddd', 
                      borderRadius: '4px' 
                    }}
                  />
                </div>
              </div>
            )}

            {/* Barra de progresso */}
            {processando && (
              <div className="mb-3">
                <div className="form-label fw-bold">
                  <i className="fas fa-cog fa-spin me-2"></i>
                  Processando...
                </div>
                <div className="progress" style={{ height: '30px' }}>
                  <div 
                    className="progress-bar progress-bar-striped progress-bar-animated bg-success"
                    role="progressbar"
                    style={{ width: `${progresso.progresso}%` }}
                  >
                    {Math.round(progresso.progresso)}%
                  </div>
                </div>
                <p className="text-center mt-2 mb-0 text-muted">
                  <small>{progresso.mensagem}</small>
                </p>
              </div>
            )}

            {/* Mensagem de erro */}
            {erro && (
              <div className="alert alert-danger">
                <i className="fas fa-exclamation-triangle me-2"></i>
                <strong>Erro:</strong> {erro}
              </div>
            )}

            {/* Dicas para melhorar OCR de manuscritos */}
            {!processando && (
              <div className="card bg-light border-0">
                <div className="card-body">
                  <h6 className="card-title fw-bold">
                    <i className="fas fa-lightbulb text-warning me-2"></i>
                    Dicas para Melhorar a Leitura de Manuscritos
                  </h6>
                  <ul className="mb-0 small">
                    {DICAS_OCR_MANUSCRITOS.map((dica, index) => (
                      <li key={index}>{dica}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleFechar}
              disabled={processando}
            >
              <i className="fas fa-times me-2"></i>
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-info"
              onClick={handleProcessarOCR}
              disabled={!arquivo || processando}
              title="Transcrever documentos impressos (digitados)"
            >
              {processando ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Processando...
                </>
              ) : (
                <>
                  <i className="fas fa-file-alt me-2"></i>
                  Transcrever Impressos
                </>
              )}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleProcessarGemini}
              disabled={!arquivo || processando}
              title="Transcrever documentos manuscritos (escritos à mão)"
            >
              {processando ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Processando...
                </>
              ) : (
                <>
                  <i className="fas fa-pen-fancy me-2"></i>
                  Transcrever Manuscritos
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
